import React, { useState, useRef, useEffect } from "react";
import Logo from "../assets/logo.png";
import { useAppContext } from "../context/AppContext";
import LanguageToggler from "./utilPages/LanguageToggler";
import { HeaderTrans, ToasterTrans } from "../utils/translations";
import LoginButton from "./utilPages/LoginButton";
import { useLocation, useNavigate } from "react-router-dom";
import LoginModal from "./utilPages/Login";
import { trackPixelEvent } from "../utils/pixel";
import CountryToggler from "./utilPages/CountryToggler";
import { Menu, X, CalendarDays, BookOpen, Lightbulb, ShoppingBag } from "lucide-react";

// Replace with standard React components for images and links
function Header() {
  const { language, toaster, setToaster, user, loading, premiumRoles, country } = useAppContext();
  const location = useLocation();
  const currentPath = location.pathname;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  const handleLoginClick = () => {
    setIsModalOpen(true); // Open modal on button click
  };

  const handleCloseModal = () => {
    setIsModalOpen(false); // Close modal
  };

  const handleRestrictedPageClick = () => {
    if (!user || !premiumRoles.includes(user?.role)) {
      //todo
      setToaster({ message: ToasterTrans[language].premium, type: "info", show: true })
    } else {
      navigate("/constructor");
    }
  }

  return (
    <>
      {/* desktop */}
      <div className={`hidden absolute lg:flex justify-between align-middle items-center px-20 w-screen z-10 text-sm font-medium top-0 bg-lightBeige h-10 border-b-[1px] ${(currentPath === "/constructor" || currentPath === "/constructor-trial" || currentPath === "/shop" || currentPath === "/about") && "border-brown"}
`}>
        <a
          href="/links"
          className="flex items-center gap-2 hover:pointer"
        >
          <img alt="logo" src={Logo} className="w-10 pb-1" />
          <span className="font-serif text-base text-pink">
            {HeaderTrans[language].onceUp}{" "}
            <span className="text-black">
              {HeaderTrans[language].onMat}
            </span>
          </span>
        </a>
       {!loading && <div className="flex justify-between gap-5">
          <a href="/schedule" onClick={() => trackPixelEvent("NavigateToStudio")} className="hover:text-pink cursor-pointer">{HeaderTrans[language].studio}</a>
          <a href="/library" onClick={() => trackPixelEvent("NavigateToLibrary")} className="hover:text-pink cursor-pointer">{HeaderTrans[language].library}</a>
          <a href={premiumRoles.includes(user?.role) ? "/constructor" : "/constructor-trial"} onClick={() => trackPixelEvent("NavigateToConstructor")} className="hover:text-pink cursor-pointer">{HeaderTrans[language].constructor}</a>
          {country === "IL" && <a href="/shop" onClick={() => trackPixelEvent("NavigateToShop")} className="hover:text-pink cursor-pointer">{HeaderTrans[language].shop}</a>}
          {/* {premiumRoles.includes(user?.role) && <a href="/live" onClick={() => trackPixelEvent("NavigateToLives")} className="hover:text-pink cursor-pointer">{HeaderTrans[language].lives}</a>} */}

          {/* // TODO uncomment when constructor and subcriptions work */}
          {/* <button onClick={() => { handleRestrictedPageClick(); trackPixelEvent("NavigateToConstructor") }} href="/constructor" className="hover:text-pink cursor-pointer">{HeaderTrans[language].constructor}</button> */}

          {/* <a href="/liveSessions" className="hover:text-pink cursor-pointer">{HeaderTrans[language].liveSessions}</a> */}
          {/* <a href="/blog" className="hover:text-pink cursor-pointer">{HeaderTrans[language].blog}</a> */}
        </div>}
        {!loading && <div className="flex gap-2">
          <LoginButton />
          <LanguageToggler />
          {/* <CountryToggler /> */}
        </div>}
      </div>
      {/* mobile */}
      <div ref={menuRef} className="lg:hidden">
        <div className="absolute z-[60] top-0 w-screen bg-white/80 backdrop-blur-sm border-b border-gray-200 flex items-center justify-between px-4 py-2">
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-700 p-1">
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <a href="/links" className="flex items-center gap-2">
            <img alt="logo" src={Logo} className="w-9" />
            <span className="font-serif text-sm text-pink">
              {HeaderTrans[language].onceUp}{" "}
              <span className="text-black">{HeaderTrans[language].onMat}</span>
            </span>
          </a>
          <div className="flex items-center gap-2 text-sm">
            <LanguageToggler onSelect={() => setIsMenuOpen(false)} />
            <LoginButton onSelect={() => setIsMenuOpen(false)} />
          </div>
        </div>
        {isMenuOpen && (
          <div className="absolute inset-x-0 top-[48px] z-40 bg-white/95 backdrop-blur-sm shadow-lg rounded-b-2xl mx-3 py-2 border border-t-0 border-gray-100 text-sm">
            <a href="/schedule" onClick={() => trackPixelEvent("NavigateToStudio")} className="flex items-center gap-3 px-5 py-3 text-gray-700 hover:bg-pink/5 hover:text-pink transition-colors">
              <CalendarDays size={18} /> <span>{HeaderTrans[language].studio}</span>
            </a>
            <a href="/library" onClick={() => trackPixelEvent("NavigateToLibrary")} className="flex items-center gap-3 px-5 py-3 text-gray-700 hover:bg-pink/5 hover:text-pink transition-colors">
              <BookOpen size={18} /> <span>{HeaderTrans[language].library}</span>
            </a>
            <a href={premiumRoles.includes(user?.role) ? "/constructor" : "/constructor-trial"} onClick={() => trackPixelEvent("NavigateToConstructor")} className="flex items-center gap-3 px-5 py-3 text-gray-700 hover:bg-pink/5 hover:text-pink transition-colors">
              <Lightbulb size={18} /> <span>{HeaderTrans[language].constructor}</span>
            </a>
            {country === "IL" && (
              <a href="/shop" onClick={() => trackPixelEvent("NavigateToShop")} className="flex items-center gap-3 px-5 py-3 text-gray-700 hover:bg-pink/5 hover:text-pink transition-colors">
                <ShoppingBag size={18} /> <span>{HeaderTrans[language].shop}</span>
              </a>
            )}
          </div>
        )}
      </div>

      <LoginModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </>
  );
}

export default Header;
